import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PersianDate from "persian-date";
import {
  isoToJalali,
  jalaliToISO,
  jalaliDaysInMonth,
  parseJalaliParts,
  toPersianDigits,
  isValidISODate,
} from "../../Page/LatestServices/components/jalaliUtils";
import "./dualDatePicker.scss";

const JALALI_MONTHS = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];

const GREGORIAN_MONTHS_FA = [
  "ژانویه",
  "فوریه",
  "مارس",
  "آوریل",
  "مه",
  "ژوئن",
  "ژوئیه",
  "اوت",
  "سپتامبر",
  "اکتبر",
  "نوامبر",
  "دسامبر",
];

const WEEK_DAYS = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
const pad2 = (n) => String(n).padStart(2, "0");

// ---- Gregorian helpers (local time, no timezone shift) ----
const gregorianDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
// Persian week index (Saturday-first) for a Gregorian date.
const gregorianFirstWeekDay = (y, m) => {
  const jsDay = new Date(y, m - 1, 1).getDay(); // 0=Sun..6=Sat
  return (jsDay + 1) % 7; // Sat=0, Sun=1, ... Fri=6
};

const parseISO = (iso) => {
  if (typeof iso !== "string") return null;
  const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return { y, m: mo, d };
};

const toISO = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

const todayISO = () => {
  const d = new Date();
  return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

// Fixed-size dual-calendar date picker.
// - value/onChange are ALWAYS Gregorian ISO "YYYY-MM-DD" (existing backend contract).
// - UI switch: شمسی (Jalali, Persian digits) / میلادی (Gregorian, Latin digits).
// - Switching calendars never corrupts the date (single ISO source of truth).
// - Popup has FIXED dimensions (same for both calendars, desktop + mobile):
//   the day grid always renders 42 cells so month length / start weekday
//   never resizes the card. Internal scroll is confined to the grid area.
// - While open, body scroll is locked and restored on close.
function DualDatePicker({
  value = "",
  onChange,
  label = "تاریخ سرویس",
  placeholder = "تاریخ را انتخاب کنید",
  disabled = false,
  shamsiOnly = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [calType, setCalType] = useState("shamsi"); // "shamsi" | "miladi"
  const [jalaliView, setJalaliView] = useState(() => {
    const jalali = isoToJalali(value);
    const p = jalali ? parseJalaliParts(jalali) : null;
    if (p) return { y: p.y, m: p.m };
    const now = new PersianDate();
    return { y: now.year(), m: now.month() };
  });
  const [gregView, setGregView] = useState(() => {
    const p = parseISO(value);
    if (p) return { y: p.y, m: p.m };
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });

  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const suppressFocusOpenRef = useRef(false);

  const isoValue = useMemo(() => {
    if (isValidISODate(value)) return value;
    return "";
  }, [value]);

  const jalaliValue = useMemo(() => (isoValue ? isoToJalali(isoValue) : ""), [isoValue]);

  // Keep both calendar views in sync when the parent value changes.
  useEffect(() => {
    if (!isoValue) return;
    const jp = jalaliValue ? parseJalaliParts(jalaliValue) : null;
    if (jp) setJalaliView({ y: jp.y, m: jp.m });
    const gp = parseISO(isoValue);
    if (gp) setGregView({ y: gp.y, m: gp.m });
  }, [isoValue, jalaliValue]);

  // Lock body scroll while the popup is open (no page scroll / no height change).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsOpen(false);
        suppressFocusOpenRef.current = true;
        inputRef.current?.focus?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const openPopup = useCallback(() => {
    if (disabled) return;
    // Refresh views from the current value on every open so stale
    // month navigation is never shown.
    if (isoValue) {
      const jp = jalaliValue ? parseJalaliParts(jalaliValue) : null;
      if (jp) setJalaliView({ y: jp.y, m: jp.m });
      const gp = parseISO(isoValue);
      if (gp) setGregView({ y: gp.y, m: gp.m });
    }
    setIsOpen(true);
  }, [disabled, isoValue, jalaliValue]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  const focusInputWithoutReopen = useCallback(() => {
    suppressFocusOpenRef.current = true;
    inputRef.current?.focus?.();
  }, []);

  const handleInputFocus = useCallback(() => {
    if (suppressFocusOpenRef.current) {
      suppressFocusOpenRef.current = false;
      return;
    }
    openPopup();
  }, [openPopup]);

  const goJalaliMonth = (delta) => {
    setJalaliView((prev) => {
      let { y, m } = prev;
      m += delta;
      while (m < 1) {
        m += 12;
        y -= 1;
      }
      while (m > 12) {
        m -= 12;
        y += 1;
      }
      return { y, m };
    });
  };

  const goGregMonth = (delta) => {
    setGregView((prev) => {
      let { y, m } = prev;
      m += delta;
      while (m < 1) {
        m += 12;
        y -= 1;
      }
      while (m > 12) {
        m -= 12;
        y += 1;
      }
      return { y, m };
    });
  };

  const pickJalaliDay = (day) => {
    const jalali = `${jalaliView.y}/${pad2(jalaliView.m)}/${pad2(day)}`;
    const iso = jalaliToISO(jalali);
    if (!iso) return;
    if (onChange) onChange(iso);
    setIsOpen(false);
    focusInputWithoutReopen();
  };

  const pickGregDay = (day) => {
    const iso = toISO(gregView.y, gregView.m, day);
    if (!isValidISODate(iso)) return;
    if (onChange) onChange(iso);
    setIsOpen(false);
    focusInputWithoutReopen();
  };

  const pickToday = () => {
    const iso = todayISO();
    if (onChange) onChange(iso);
    const jp = parseJalaliParts(isoToJalali(iso));
    if (jp) setJalaliView({ y: jp.y, m: jp.m });
    const gp = parseISO(iso);
    if (gp) setGregView({ y: gp.y, m: gp.m });
    setIsOpen(false);
    focusInputWithoutReopen();
  };

  const displayValue = useMemo(() => {
    if (!isoValue) return "";
    if (shamsiOnly || calType === "shamsi") {
      return jalaliValue ? toPersianDigits(jalaliValue) : "";
    }
    const p = parseISO(isoValue);
    if (!p) return "";
    return `${p.y}/${pad2(p.m)}/${pad2(p.d)}`;
  }, [isoValue, jalaliValue, calType, shamsiOnly]);

  // ---- Fixed 42-cell grids (no resize across months) ----
  const jalaliCells = useMemo(() => {
    const days = jalaliDaysInMonth(jalaliView.y, jalaliView.m);
    let first = 0;
    try {
      first = new PersianDate([jalaliView.y, jalaliView.m, 1]).day();
    } catch {
      first = 0;
    }
    const cells = [];
    for (let i = 0; i < first; i += 1) {
      cells.push({ key: `e-${i}`, empty: true });
    }
    for (let d = 1; d <= days; d += 1) {
      const key = `${jalaliView.y}/${pad2(jalaliView.m)}/${pad2(d)}`;
      cells.push({ key, day: d, selected: key === jalaliValue });
    }
    while (cells.length < 42) {
      cells.push({ key: `t-${cells.length}`, empty: true });
    }
    return cells;
  }, [jalaliView, jalaliValue]);

  const gregCells = useMemo(() => {
    const days = gregorianDaysInMonth(gregView.y, gregView.m);
    const first = gregorianFirstWeekDay(gregView.y, gregView.m);
    const sel = parseISO(isoValue);
    const cells = [];
    for (let i = 0; i < first; i += 1) {
      cells.push({ key: `e-${i}`, empty: true });
    }
    for (let d = 1; d <= days; d += 1) {
      const selected = sel != null && sel.y === gregView.y && sel.m === gregView.m && sel.d === d;
      cells.push({ key: `d-${d}`, day: d, selected });
    }
    while (cells.length < 42) {
      cells.push({ key: `t-${cells.length}`, empty: true });
    }
    return cells;
  }, [gregView, isoValue]);

  const isShamsi = shamsiOnly || calType === "shamsi";

  return (
    <div className="dual-date" ref={fieldRef} dir="rtl">
      {label && <span className="dual-date__label">{label}</span>}
      <div className="dual-date__input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="dual-date__input"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onFocus={handleInputFocus}
          onBlur={() => {
            suppressFocusOpenRef.current = false;
          }}
          onClick={openPopup}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
              e.preventDefault();
              openPopup();
            }
          }}
          aria-haspopup="dialog"
          role="combobox"
          aria-autocomplete="none"
          aria-expanded={isOpen}
          aria-controls={isOpen ? "dual-date-calendar" : undefined}
        />
        <span className="dual-date__icon" aria-hidden>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="17" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </span>
      </div>

      {isOpen && !disabled && (
        <div className="dual-date__overlay" onClick={closePopup}>
          <div
            className="dual-date__card"
            id="dual-date-calendar"
            role="dialog"
            aria-modal="true"
            aria-label="انتخاب تاریخ"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dual-date__head">
              <p className="dual-date__title">انتخاب تاریخ</p>
              <button type="button" className="dual-date__close" onClick={() => { closePopup(); focusInputWithoutReopen(); }} aria-label="بستن تقویم">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {!shamsiOnly && <div className="dual-date__type" role="tablist" aria-label="نوع تاریخ">
              <span className="dual-date__type-label">نوع تاریخ:</span>
              <div className="dual-date__type-btns">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isShamsi}
                  className={`dual-date__type-btn${isShamsi ? " is-active" : ""}`}
                  onClick={() => setCalType("shamsi")}
                >
                  شمسی
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isShamsi}
                  className={`dual-date__type-btn${!isShamsi ? " is-active" : ""}`}
                  onClick={() => setCalType("miladi")}
                >
                  میلادی
                </button>
              </div>
            </div>}

            <div className="dual-date__nav">
              <button type="button" className="dual-date__nav-btn" onClick={() => (isShamsi ? goJalaliMonth(1) : goGregMonth(1))} aria-label="ماه بعد">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <div className="dual-date__month">
                {isShamsi ? (
                  <>
                    <span>{JALALI_MONTHS[jalaliView.m - 1]}</span>
                    <span>{toPersianDigits(jalaliView.y)}</span>
                  </>
                ) : (
                  <>
                    <span>{GREGORIAN_MONTHS_FA[gregView.m - 1]}</span>
                    <span dir="ltr">{gregView.y}</span>
                  </>
                )}
              </div>
              <button type="button" className="dual-date__nav-btn" onClick={() => (isShamsi ? goJalaliMonth(-1) : goGregMonth(-1))} aria-label="ماه قبل">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </div>

            <div className="dual-date__week">
              {WEEK_DAYS.map((d) => (
                <div key={d} className="dual-date__weekday">{d}</div>
              ))}
            </div>
            <div className="dual-date__grid">
              {(isShamsi ? jalaliCells : gregCells).map((c) =>
                c.empty ? (
                  <div key={c.key} className="dual-date__day dual-date__day--empty" />
                ) : (
                  <button
                    key={c.key}
                    type="button"
                    className={`dual-date__day${c.selected ? " is-selected" : ""}`}
                    onClick={() => (isShamsi ? pickJalaliDay(c.day) : pickGregDay(c.day))}
                  >
                    {isShamsi ? toPersianDigits(c.day) : c.day}
                  </button>
                )
              )}
            </div>

            <div className="dual-date__foot">
              <button type="button" className="dual-date__today" onClick={pickToday}>
                امروز
              </button>
              <span className="dual-date__current">{displayValue || "—"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DualDatePicker;
